import React, { lazy } from "react";
import { Route } from "react-router";
import ProtectedRoute from "../components/ProtectedRoute";
import RouteErrorBoundary from "../components/RouteErrorBoundary";
import { protectedAreaProps } from "./requireAuth";
import TeacherLayout from "../components/TeacherLayout";
import TeacherProtectedRoute from "../components/TeacherProtectedRoute";
const TeacherOverview = lazy(() => import("../pages/teacher/TeacherOverview"));
const TeacherProfile = lazy(() => import("../pages/teacher/TeacherProfile"));
const MyCourses = lazy(() => import("../pages/teacher-analytics/MyCourses"));
const CourseDetailsPage = lazy(() => import("../pages/teacher/CourseDetailsPage"));
const CreateCourse = lazy(() => import("../pages/teacher/CreateCourse"));
const ModuleContents = lazy(() => import("../pages/teacher/ModuleContents"));
const TeacherStudents = lazy(() => import("../pages/teacher/TeacherStudents"));
const TeacherStudentEnrollments = lazy(() => import("../pages/teacher/TeacherStudentEnrollments"));
const TeacherStudentProfile = lazy(() => import("../pages/teacher/TeacherStudentProfile"));
const AnalyticsDashboard = lazy(() => import("../pages/teacher/AnalyticsDashboard"));
const TeacherAssignments = lazy(() => import("../pages/teacher/TeacherAssignments"));
const TeacherMessages = lazy(() => import("../pages/teacher/TeacherMessages"));
const TeacherEnrollmentAudit = lazy(() => import("../pages/teacher/TeacherEnrollmentAuditResponsive"));
const TeacherOrders = lazy(() => import("../pages/teacher/TeacherOrders"));
const MCQManagement = lazy(() => import("../pages/teacher/MCQManagement"));
const MCQSetDetail = lazy(() => import("../pages/teacher/MCQSetDetail"));
const CreateMCQSet = lazy(() => import("../pages/teacher/CreateMCQSet"));
const EditMCQSet = lazy(() => import("../pages/teacher/EditMCQSet"));
const AddQuestion = lazy(() => import("../pages/teacher/AddQuestion"));
const EditQuestion = lazy(() => import("../pages/teacher/EditQuestion"));
const TeacherCoupons = lazy(() => import("../pages/teacher/TeacherCoupons"));
const CreateCoupon = lazy(() => import("../pages/teacher/CreateCoupon"));
const CouponDetail = lazy(() => import("../pages/teacher/CouponDetail"));
const EditCoupon = lazy(() => import("../pages/teacher/EditCoupon"));
const CouponLookupPage = lazy(() => import("../pages/teacher/CouponLookup"));
const CouponRedemptions = lazy(() => import("../pages/teacher/CouponRedemptions"));
const CouponTesting = lazy(() => import("../pages/teacher/CouponTesting"));
const SecurityDashboard = lazy(() => import("../pages/teacher/SecurityDashboard"));
const RiskBasedSecurityDashboard = lazy(() => import("../components/security/RiskBasedSecurityDashboard"));
const SuspiciousActivityAnalysis = lazy(() => import("../pages/teacher/SuspiciousActivityAnalysis"));
const EmergencyControls = lazy(() => import("../pages/teacher/EmergencyControls"));
const ThresholdManagement = lazy(() => import("../pages/teacher/ThresholdManagement"));
const SessionsPage = lazy(() => import("../pages/SessionsPage"));
const LiveSessionNotify = lazy(() => import("../pages/teacher/LiveSessionNotify"));
const TeacherBroadcastPage = lazy(() => import("../pages/teacher/TeacherBroadcastPage"));
const NotFound = lazy(() => import("../pages/NotFound"));

export const teacherRoutes = (
  <Route
    path="/teacher"
    {...protectedAreaProps}
    element={
      // requireAuth redirects signed-out visitors before anything renders; ProtectedRoute
      // covers a session that ends mid-visit. Each page's TeacherProtectedRoute still
      // checks the role and privacy policy.
      <ProtectedRoute>
        <TeacherLayout />
      </ProtectedRoute>
    }
  >
    <Route errorElement={<RouteErrorBoundary />}>
      <Route
        path="overview"
        element={
          <TeacherProtectedRoute>
            <TeacherOverview />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="profile"
        element={
          <TeacherProtectedRoute>
            <TeacherProfile />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="my-courses"
        element={
          <TeacherProtectedRoute>
            <MyCourses />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="create-course"
        element={
          <TeacherProtectedRoute>
            <CreateCourse />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="mcq"
        element={
          <TeacherProtectedRoute>
            <MCQManagement />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="mcq/create"
        element={
          <TeacherProtectedRoute>
            <CreateMCQSet />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="mcq/:id"
        element={
          <TeacherProtectedRoute>
            <MCQSetDetail />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="mcq/:id/edit"
        element={
          <TeacherProtectedRoute>
            <EditMCQSet />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="mcq/:setId/questions/add"
        element={
          <TeacherProtectedRoute>
            <AddQuestion />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="mcq/:setId/questions/:quesId/edit"
        element={
          <TeacherProtectedRoute>
            <EditQuestion />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="coupons"
        element={
          <TeacherProtectedRoute>
            <TeacherCoupons />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="coupons/create"
        element={
          <TeacherProtectedRoute>
            <CreateCoupon />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="coupons/lookup"
        element={
          <TeacherProtectedRoute>
            <CouponLookupPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="coupons/:id"
        element={
          <TeacherProtectedRoute>
            <CouponDetail />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="coupons/:id/edit"
        element={
          <TeacherProtectedRoute>
            <EditCoupon />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="coupons/:couponId/redemptions"
        element={
          <TeacherProtectedRoute>
            <CouponRedemptions />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="redemptions"
        element={
          <TeacherProtectedRoute>
            <CouponRedemptions />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="coupons/testing"
        element={
          <TeacherProtectedRoute>
            <CouponTesting />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="security"
        element={
          <TeacherProtectedRoute>
            <RiskBasedSecurityDashboard />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="security/legacy"
        element={
          <TeacherProtectedRoute>
            <SecurityDashboard />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="security/suspicious-activity/:couponId"
        element={
          <TeacherProtectedRoute>
            <SuspiciousActivityAnalysis />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="security/emergency"
        element={
          <TeacherProtectedRoute>
            <EmergencyControls />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="security/thresholds"
        element={
          <TeacherProtectedRoute>
            <ThresholdManagement />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="course/:code"
        element={
          <TeacherProtectedRoute>
            <CourseDetailsPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="course/:code/module/:moduleCode"
        element={
          <TeacherProtectedRoute>
            <ModuleContents />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="students"
        element={
          <TeacherProtectedRoute>
            <TeacherStudents />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="students/:studentId/profile"
        element={
          <TeacherProtectedRoute>
            <TeacherStudentProfile />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="students/:studentId/enrollments"
        element={
          <TeacherProtectedRoute>
            <TeacherStudentEnrollments />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="audit/enrollments"
        element={
          <TeacherProtectedRoute>
            <TeacherEnrollmentAudit />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="orders"
        element={
          <TeacherProtectedRoute>
            <TeacherOrders />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="analytics"
        element={
          <TeacherProtectedRoute>
            <AnalyticsDashboard />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="assignments"
        element={
          <TeacherProtectedRoute>
            <TeacherAssignments />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="messages"
        element={
          <TeacherProtectedRoute>
            <TeacherMessages />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="sessions"
        element={
          <TeacherProtectedRoute>
            <SessionsPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="live-session"
        element={
          <TeacherProtectedRoute>
            <LiveSessionNotify />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="broadcasts"
        element={
          <TeacherProtectedRoute>
            <TeacherBroadcastPage />
          </TeacherProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Route>
);
